import React from "react";
import { Link } from "react-router-dom";
import {
  MdAccountBalanceWallet,
  MdAdd,
  MdInbox,
  MdLayers,
  MdMenuBook,
  MdPeopleAlt,
  MdSchool,
} from "react-icons/md";
import { Skeleton } from "components/ui/Skeleton";
import Widget from "components/widget/Widget";
import { useAppLanguage } from "hooks/useAppLanguage";
import { instructorAuthService } from "services/instructorAuthService";
import { instructorBundlesService } from "services/instructorBundlesService";
import { instructorCoursesService, type InstructorCourse } from "services/instructorCoursesService";
import { instructorStudentsService } from "services/instructorStudentsService";
import {
  DashboardEmptyState,
  DashboardSection,
} from "components/dashboard/DashboardUi";
import { UI_ACTIONS_GRID, UI_DASHBOARD_STACK, UI_STATS_GRID } from "lib/uiClasses";
import { formatLocaleDate } from "lib/localeFormat";

type InstructorDashboardStats = {
  courses: number;
  students: number;
  bundles: number;
};

export default function InstructorDashboard() {
  const language = useAppLanguage();
  const isArabic = language === "ar";

  const [loading, setLoading] = React.useState(true);
  const [instructorName, setInstructorName] = React.useState<string | null>(null);
  const [stats, setStats] = React.useState<InstructorDashboardStats>({
    courses: 0,
    students: 0,
    bundles: 0,
  });
  const [recentCourses, setRecentCourses] = React.useState<InstructorCourse[]>([]);

  const nowLabel = React.useMemo(() => {
    return formatLocaleDate(new Date(), isArabic, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [isArabic]);

  React.useEffect(() => {
    let cancelled = false;
    instructorAuthService
      .getProfile()
      .then((profile) => {
        if (!cancelled) {
          setInstructorName(profile?.name || profile?.email || profile?.phone || null);
        }
      })
      .catch(() => {
        if (!cancelled) setInstructorName(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const [coursesRes, studentsRes, bundlesRes] =
          await Promise.allSettled([
            instructorCoursesService.list({ limit: 5, sort_by: "id", sort_direction: "desc" }),
            instructorStudentsService.list({ limit: 1 }),
            instructorBundlesService.list({ limit: 1 }),
          ]);

        if (cancelled) return;

        const courses = coursesRes.status === "fulfilled" ? coursesRes.value : null;
        const students = studentsRes.status === "fulfilled" ? studentsRes.value : null;
        const bundles = bundlesRes.status === "fulfilled" ? bundlesRes.value : null;

        setStats({
          courses: courses?.meta?.total ?? courses?.data?.length ?? 0,
          students: students?.meta?.total ?? students?.data?.length ?? 0,
          bundles: bundles?.meta?.total ?? bundles?.data?.length ?? 0,
        });
        setRecentCourses(Array.isArray(courses?.data) ? courses!.data : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const quickActions = [
    {
      label: isArabic ? "إنشاء كورس جديد" : "Create course",
      href: "/instructor/courses/new",
      icon: <MdMenuBook className="h-4 w-4" />,
    },
    {
      label: isArabic ? "عرض مجمّع جديد" : "New bundle",
      href: "/instructor/bundles/new",
      icon: <MdLayers className="h-4 w-4" />,
    },
    {
      label: isArabic ? "الطلاب" : "Students",
      href: "/instructor/students",
      icon: <MdPeopleAlt className="h-4 w-4" />,
    },
    {
      label: isArabic ? "حساباتي" : "My accounts",
      href: "/instructor/accounts",
      icon: <MdAccountBalanceWallet className="h-4 w-4" />,
    },
  ];

  return (
    <div className={UI_DASHBOARD_STACK} dir={isArabic ? "rtl" : "ltr"}>
      {/* Welcome header */}
      <header className="apple-dashboard-hero apple-dashboard-hero-instructor relative isolate overflow-hidden p-5 text-white sm:p-7">
        <div className="pointer-events-none absolute -left-16 -top-20 h-64 w-64 rounded-full bg-white/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 right-1/3 h-48 w-48 rounded-full bg-yellow-200/15 blur-3xl" />
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <span className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] font-bold text-white/90 backdrop-blur-sm">
              <MdSchool className="h-4 w-4" />
              {isArabic ? "بوابة المحاضر" : "Instructor Portal"}
            </span>
            <h1 className="truncate text-2xl font-extrabold text-white sm:text-3xl">
              {isArabic ? "بوابة المحاضر" : "Instructor Dashboard"}
            </h1>
            <p className="mt-1 text-sm text-orange-50/85 sm:text-base">
              {isArabic ? "نظرة سريعة على نشاطك التدريسي" : "Quick overview of your teaching activity"}
            </p>
            {instructorName ? (
              <p className="mt-3 truncate text-sm font-medium text-white/90">
                {isArabic ? `مرحبًا، ${instructorName}` : `Welcome, ${instructorName}`}
              </p>
            ) : null}
          </div>
          <p className="text-xs leading-snug text-white/70 sm:text-sm sm:text-end">
            {nowLabel}
          </p>
        </div>
      </header>

      {/* Key stats — 1 col mobile → 2 sm → 3 lg → 4 xl */}
      <DashboardSection
        title={isArabic ? "مؤشرات سريعة" : "Key stats"}
        subtitle={isArabic ? "أهم أرقام حسابك" : "Snapshot of your workspace"}
      >
        <div className={UI_STATS_GRID}>
          <Link to="/instructor/courses" className="block h-full">
            <Widget
              icon={<MdMenuBook className="h-6 w-6" />}
              title={isArabic ? "الكورسات" : "Courses"}
              subtitle={loading ? "…" : String(stats.courses)}
            />
          </Link>
          <Link to="/instructor/students" className="block h-full">
            <Widget
              icon={<MdPeopleAlt className="h-6 w-6" />}
              title={isArabic ? "الطلاب" : "Students"}
              subtitle={loading ? "…" : String(stats.students)}
            />
          </Link>
          <Link to="/instructor/bundles" className="block h-full">
            <Widget
              icon={<MdLayers className="h-6 w-6" />}
              title={isArabic ? "عروض الكورسات" : "Course bundles"}
              subtitle={loading ? "…" : String(stats.bundles)}
            />
          </Link>
        </div>
      </DashboardSection>

      {/* Quick actions */}
      <DashboardSection
        title={isArabic ? "إجراءات سريعة" : "Quick actions"}
        subtitle={isArabic ? "اختصارات للمهام الشائعة" : "Shortcuts to common tasks"}
      >
        <div className={UI_ACTIONS_GRID}>
          {quickActions.map((action) => (
            <Link
              key={action.href + action.label}
              to={action.href}
              className="apple-action-tile flex min-h-[52px] items-center gap-3 px-3 py-3 text-sm font-semibold sm:px-4"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-lightPrimary text-brand-500 dark:bg-navy-700 dark:text-white">
                {action.icon}
              </span>
              <span className="flex-1 truncate">{action.label}</span>
              <MdAdd className="h-4 w-4 shrink-0 opacity-60" />
            </Link>
          ))}
        </div>
      </DashboardSection>

      {/* Recent courses */}
      <div>
          <DashboardSection
            title={isArabic ? "أحدث الكورسات" : "Recent courses"}
            action={
              <Link
                to="/instructor/courses"
                className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600"
              >
                {isArabic ? "عرض الكل" : "View all"}
              </Link>
            }
          >
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} className="h-10 w-full rounded-lg" />
                ))}
              </div>
            ) : recentCourses.length === 0 ? (
              <DashboardEmptyState
                icon={<MdInbox className="h-4 w-4" />}
                title={isArabic ? "لا توجد كورسات بعد" : "No courses yet"}
                description={
                  isArabic
                    ? "ابدأ بإنشاء أول كورس لك"
                    : "Start by creating your first course"
                }
              />
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-white/10">
                {recentCourses.map((course) => (
                  <li key={course.id}>
                    <Link
                      to={`/instructor/courses/${course.id}/overview`}
                      className="flex items-center justify-between gap-3 py-3 text-sm hover:text-brand-600 dark:hover:text-brand-300"
                    >
                      <span className="min-w-0 flex-1 truncate font-semibold text-gray-800 dark:text-white">
                        {course.title}
                      </span>
                      <span className="shrink-0 whitespace-nowrap text-[11px] text-gray-400 sm:text-xs">
                        #{course.id}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </DashboardSection>
        </div>
    </div>
  );
}
